import React from 'react'
import { Button, Popover, Position, Intent } from '@blueprintjs/core'
import api from '../../shared/api'

const emptyLaser = () => ({ wavelength: '', minimum_od: '', mode: '', minimum_lb: '', minimum_rb: '', minimum_ab: '' })

const laserCriteria = laser => [
  laser.wavelength && `${laser.wavelength}nm`,
  laser.minimum_od !== '' && `OD${laser.minimum_od}`,
  laser.mode,
  laser.minimum_lb !== '' && `LB${laser.minimum_lb}`,
  laser.minimum_rb !== '' && `RB${laser.minimum_rb}`,
  laser.minimum_ab !== '' && `AB${laser.minimum_ab}`
].filter(Boolean).join(' ')

export default class ProtectionPicker extends React.Component {
  state = { open: false, lasers: [emptyLaser()], loading: false, error: null }
  request = 0

  componentWillUnmount() {
    this.request++
  }

  changeLasers = lasers => {
    this.request++
    this.setState({ lasers, error: null, loading: false })
  }

  updateLaser = (index, field, value) => {
    this.changeLasers(this.state.lasers.map((laser, i) =>
      i === index ? { ...laser, [field]: value } : laser
    ))
  }

  clear = () => {
    this.changeLasers([emptyLaser()])
    this.props.onClear()
  }

  search = event => {
    event.preventDefault()
    const request = ++this.request
    this.setState({ loading: true, error: null })
    api.post('/api/filters/search.json', { lasers: this.state.lasers }, this.props.token)
      .then(({ filters }) => {
        if (request === this.request) {
          this.setState({ loading: false, open: false })
          this.props.onMatch(Object.keys(filters))
        }
      })
      .catch(() => {
        if (request === this.request) {
          this.setState({ loading: false, error: 'Search failed. Check your requirements and try again.' })
        }
      })
  }

  render() {
    const { open, lasers, loading, error } = this.state
    return (
      <Popover isOpen={open} onInteraction={isOpen => this.setState({ open: isOpen })}
        position={Position.BOTTOM_RIGHT} inheritDarkTheme={false}>
        <Button iconName="search" active={this.props.active}
          aria-label={this.props.active ? 'Edit eyewear search' : 'Search eyewear'}
          title={this.props.active ? 'Edit eyewear search' : 'Search eyewear'}>
          {this.props.active
            ? lasers.map((laser, index) => (
              <span className="pt-tag pt-minimal" key={index}>{laserCriteria(laser)}</span>
            ))
            : 'Search eyewear…'}
        </Button>
          <form style={{ padding: 16, width: 380, maxWidth: '90vw' }} onSubmit={this.search}>
            <h5>Find matching eyewear</h5>
            <p>Matches must meet every minimum for every laser. Ratings must be whole numbers of at least 3. Leave unused ratings blank. LB requires a CE mode; AB uses the selected mode, or mode-free ratings if unspecified.</p>
            <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
              {lasers.map((laser, index) => (
                <fieldset key={index} style={{ marginBottom: 15 }}>
                  <legend>Laser {index + 1}</legend>
                  <label className="pt-label">
                    Wavelength (nm)
                    <input className="pt-input" type="number" min="1" step="1" required
                      value={laser.wavelength} onChange={e => this.updateLaser(index, 'wavelength', e.target.value)} />
                  </label>
                  <label className="pt-label">
                    Minimum OD
                    <input className="pt-input" type="number" min="3" step="1" required={!laser.minimum_lb && !laser.minimum_rb && !laser.minimum_ab}
                      value={laser.minimum_od} onChange={e => this.updateLaser(index, 'minimum_od', e.target.value)} />
                  </label>
                  <label className="pt-label">
                    CE mode
                    <div className="pt-select">
                      <select value={laser.mode} required={laser.minimum_lb !== ''}
                        onChange={e => this.updateLaser(index, 'mode', e.target.value)}>
                        <option value="">Not specified</option>
                        <option value="D">D — continuous</option>
                        <option value="I">I — pulsed</option>
                        <option value="R">R — giant pulsed</option>
                        <option value="M">M — mode-locked</option>
                      </select>
                    </div>
                  </label>
                  <label className="pt-label">
                    Minimum LB
                    <input className="pt-input" type="number" min="3" step="1"
                      value={laser.minimum_lb} onChange={e => this.updateLaser(index, 'minimum_lb', e.target.value)} />
                  </label>
                  {['rb', 'ab'].map(metric => (
                    <label className="pt-label" key={metric}>
                      Minimum {metric.toUpperCase()}
                      <input className="pt-input" type="number" min="3" step="1"
                        value={laser[`minimum_${metric}`]}
                        onChange={e => this.updateLaser(index, `minimum_${metric}`, e.target.value)} />
                    </label>
                  ))}
                  {lasers.length > 1 && <Button type="button" text="Remove laser" onClick={() => this.changeLasers(lasers.filter((_, i) => i !== index))} />}
                </fieldset>
              ))}
            </div>
            <Button type="button" iconName="add" text="Add another laser" disabled={lasers.length >= 50}
              onClick={() => this.changeLasers([...lasers, emptyLaser()])} />
            <Button type="submit" intent={Intent.PRIMARY} loading={loading} text="Find matching eyewear" />
            {this.props.active && <Button type="button" text="Clear search" onClick={this.clear} />}
            <div aria-live="polite">
              {error && <p role="alert">{error}</p>}
            </div>
          </form>
      </Popover>
    )
  }
}
